'use client';

import { useCallback } from 'react';
import { Connection, PublicKey } from '@solana/web3.js';
import {
	IWallet,
	TokenFaucet,
	VELOCITY_PROGRAM_ID,
} from '@velocity-exchange/sdk';
import { getUserKey } from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { getSubAccountStorageKey } from 'src/utils/account';
import { useFetchAndSubscribeSubAccounts } from '../user/useFetchAndSubscribeSubaccounts/useFetchAndSubscribeSubaccounts';
import { useSyncDeletedSubaccountsPubkey } from '../user/useFetchAndSubscribeSubaccounts/useSyncDeletedSubaccounts';
import useVelocityClient from '../useVelocityClient';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';

export const useLinkWallet = () => {
	const velocityClient = useVelocityClient();
	const getVelocityStore = useVelocityStore((s) => s.get);

	const setVelocityStore = useVelocityStore((s) => s.set);
	const accountsSet = useVelocityAccountsStore((s) => s.set);
	const resetAccountsState = useVelocityAccountsStore(
		(s) => s.resetAccountsState
	);

	const { fetchAndSubscribeSubAccounts } = useFetchAndSubscribeSubAccounts();
	const { syncDeletedSubaccountsPubkey } = useSyncDeletedSubaccountsPubkey();

	const linkWalletToVelocityClient = useCallback(async (): Promise<boolean> => {
		if (!velocityClient) return false;

		// Skip regular connection logic when using account emulation
		const urlSearchParams = new URLSearchParams(window.location.search);

		if (
			urlSearchParams.has('authority') ||
			urlSearchParams.has('userAccount')
		) {
			return false;
		}

		// The connected adapter can briefly report no publicKey (extension autoconnect /
		// mid-reconnect). updateWallet derives `this.authority = wallet.publicKey`
		// synchronously and would crash in getUserStatsAccountPublicKey (null.toBuffer()),
		// leaving the client half-updated and triggering a render loop. Bail so the connect
		// effect retries once the key resolves.
		if (!getVelocityStore().wallet.current.adapter?.publicKey) {
			return false;
		}

		// we reset the accounts state first, so that account state dependencies are able to react to the state reset.
		// this prevents a race condition where the account state still exists in the store, but the user client is unsubscribed,
		// causing data access errors (especially in User.getUserAccount())
		resetAccountsState();
		await Promise.all(velocityClient.unsubscribeUsers());
		// we reset the account state again, in case there is a race condition that updates the account state just before the
		// users are fully unsubscribed
		resetAccountsState();

		const wallet = getVelocityStore().wallet;
		const authority = wallet.current.adapter?.publicKey as PublicKey;

		const connectionStateForWallet = wallet.connectionState;

		const subAccountStorageKey = getSubAccountStorageKey(authority, Env.sdkEnv);
		const storedSubAccountIdValue = subAccountStorageKey
			? (window?.localStorage?.getItem(subAccountStorageKey) ?? undefined)
			: undefined;

		const subAccountId = storedSubAccountIdValue
			? Number(storedSubAccountIdValue)
			: undefined;

		const walletAdapter = wallet.current.adapter as IWallet;

		// Re-check after the awaits above: the adapter's publicKey can be nulled mid-flight
		// (extension reconnect). updateWallet reads it synchronously, so guarding here keeps
		// a null authority from reaching getUserStatsAccountPublicKey.
		if (!walletAdapter?.publicKey) {
			return false;
		}

		const success = await velocityClient.updateWallet(
			walletAdapter,
			undefined,
			subAccountId,
			true,
			undefined
		);

		if (!success) {
			console.log('Error subscribing to users for new wallet');
		}

		const connection = getVelocityStore().connection.current;
		if (connection) {
			const mockUsdcFaucet = new TokenFaucet(
				connection as Connection,
				wallet.current.adapter as IWallet,
				new PublicKey(VELOCITY_PROGRAM_ID),
				new PublicKey(DEFAULT_PUBLIC_KEY.toString())
			);

			// add faucet to state
			setVelocityStore((state) => {
				state.faucet = mockUsdcFaucet;
			});
		}

		let userAccountExists = false;

		try {
			fetchAndSubscribeSubAccounts();
			syncDeletedSubaccountsPubkey(authority as PublicKey);

			let initialAccountId = velocityClient?.activeSubAccountId ?? 0;
			const initialAuthority = velocityClient?.authority;

			try {
				// we can do this to determine if user exists instead of doing await user.exists() since we only just fetched subaccounts above
				userAccountExists = !!velocityClient
					?.getUser(initialAccountId)
					.getUserAccount();
			} catch {
				userAccountExists = false;
			}

			// ensure that the user account exists before setting the user key
			const authorityHasVelocityUsers = velocityClient.getUsers().length > 0;
			if (!userAccountExists && authorityHasVelocityUsers) {
				const firstUser = velocityClient.getUsers()[0];
				initialAccountId = firstUser.getUserAccountOrThrow().subAccountId;
			}

			const userKey = getUserKey(initialAccountId, initialAuthority);

			accountsSet((s) => {
				s.currentUserKey = userKey;
			});
		} catch {
			// user account doesn't exist
			// TODO : I think this isn't true anymore - it gracefully handles there being no user account and we should let the error bubble if we encounter one here.
		}

		const authorityString = authority?.toString();

		connectionStateForWallet?.progress('ClientConnected');
		connectionStateForWallet?.progress('AdapterConnected');

		setVelocityStore((s) => {
			s.wallet.connectionState = connectionStateForWallet;
			// @ts-ignore
			s.wallet.currentPublicKeyString = authorityString;
		});

		connectionStateForWallet.progress('SubaccountsSubscribed');

		return true;
	}, [
		velocityClient,
		getVelocityStore,
		setVelocityStore,
		accountsSet,
		resetAccountsState,
		fetchAndSubscribeSubAccounts,
		syncDeletedSubaccountsPubkey,
	]);

	return {
		linkWalletToVelocityClient,
	};
};
